import { useCallback, useEffect, useRef, useState } from 'react'

import { MAIN_CONTENT_SCROLL_TOP_OFFSET } from '@/shared/docs-shared.const'
import { getMainContentScrollContainer } from '@/shared/utils/getMainContentScrollContainer'

import type { ContentNavHeading } from '../content-nav.interface'

export function useActiveHeading(headings: ContentNavHeading[]): {
  activeId: string
  scrollToHeading: (headingId: string) => void
} {
  const [activeId, setActiveId] = useState(headings[0]?.id ?? '')
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const rafRef = useRef<number | undefined>(undefined)

  const scrollToHeading = useCallback((headingId: string) => {
    const element = document.getElementById(headingId)
    const scrollContainer = getMainContentScrollContainer()
    if (!element || !scrollContainer) return

    isScrollingRef.current = true
    setActiveId(headingId)

    const containerRect = scrollContainer.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    const offset = elementRect.top - containerRect.top + scrollContainer.scrollTop - MAIN_CONTENT_SCROLL_TOP_OFFSET

    scrollContainer.scrollTo({ top: offset, behavior: 'smooth' })

    clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false
    }, 800)
  }, [])

  useEffect(() => {
    if (headings.length === 0) return

    setActiveId(headings[0]?.id ?? '')
  }, [headings])

  useEffect(() => {
    const scrollContainer = getMainContentScrollContainer()
    if (!scrollContainer || headings.length === 0) return

    const headingIds = headings.map((h) => h.id)

    function updateActiveOnScroll() {
      if (isScrollingRef.current) return

      const containerTop = scrollContainer!.getBoundingClientRect().top + MAIN_CONTENT_SCROLL_TOP_OFFSET
      let closestId = headingIds[0]
      let closestDistance = Infinity

      for (const id of headingIds) {
        const el = document.getElementById(id)
        if (!el) continue

        const rect = el.getBoundingClientRect()
        const distance = rect.top - containerTop

        if (distance <= 20 && Math.abs(distance) < closestDistance) {
          closestDistance = Math.abs(distance)
          closestId = id
        }
      }

      if (closestId) {
        setActiveId(closestId)
      }
    }

    function onScroll() {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(updateActiveOnScroll)
    }

    scrollContainer.addEventListener('scroll', onScroll, { passive: true })
    updateActiveOnScroll()

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll)
      clearTimeout(scrollTimeoutRef.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [headings])

  return { activeId, scrollToHeading }
}
